import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MultipleTabsModal from '../Modals/MultipleTabsModal';
import Button from '../Button/Button';
import {
  handleCreateAndAssociateTag, handleCreatedNotAssociatedTag, handleBulkTagsAndSitesAssociation,
} from '../../utils/tagUtils';
import { useToasts, useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges } from '../../hooks';
import { getTags } from '../../utils/api/tag';
import { SearchIcon } from '../Icons/Icons';
import DoneIcon from '../../../../assets/images/iconsV2/done.svg';
import DashIcon from '../../../../assets/images/iconsV2/dash.svg';
import { removeTag } from '../../utils/api/site';
import { redirectToTags } from '../../utils/navigation';

const DEFAULT_EXISTING_TAGS = [];

const DEFAULT_PARAMS = {
  includeSiteData: false,
  name: '',
  sortBy: '',
  direction: '',
  limit: 1000,
  offset: 0,
};

const TagModal = ({
  handleClose, onCreate = () => { }, onDelete = () => { }, isTagAsTabShowing, isCreateNewTabShowing, title, orgId, siteIds = null, isStorybook = false, existingTags = DEFAULT_EXISTING_TAGS, params = DEFAULT_PARAMS, isTagShowPage = false, isAllSelected = false, onTagsUpdated = null,
}) => {
  const [tagName, setTagName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [inlineError, setInlineError] = useState('');
  const { showToast } = useToasts();
  const [tags, setTags] = useState([]);
  const [filteredTags, setFilteredTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tagsToAdd, setTagsToAdd] = useState([]);
  const [tagsToRemove, setTagsToRemove] = useState([]);
  const [updatedNonCommonTags, setUpdatedNonCommonTags] = useState([]);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);

  const hasUnsavedChanges = tagName !== '' || tagsToAdd.length > 0 || (tagsToAdd.length > 0 && existingTags.includes(tagsToAdd)) || tagsToRemove.length > 0;

  // Extract the complex expression to a variable for useEffect dependency
  const unsavedChangesDependency = tagName !== '' || tagsToAdd.length > 0 || (tagsToAdd.length > 0 && existingTags.includes(tagsToAdd)) || tagsToRemove.length > 0;

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, unsavedChangesDependency]); // unsavedChangesDependency - track changes to unsavedChangesDependency for unsaved changes if isUnsavedChanges was set to false on previous changes

  useUnsavedChangesWarning(isUnsavedChanges);

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: handleClose,
  });

  // Fetch tags
  useEffect(() => {
    if (isStorybook) {
      // Mock data for Storybook testing
      const mockTags = [
        { id: 1, name: 'Tag 1' },
        { id: 2, name: 'Tag 2' },
        { id: 3, name: 'Tag 3' },
        { id: 4, name: 'Tag 4' },
      ];
      setTags(mockTags);
      setFilteredTags(mockTags);
      setTagsToAdd(existingTags);
      return;
    }

    const fetchTags = async () => {
      try {
        const response = await getTags(orgId, {
          includeSiteData: false,
          limit: 1000,
          offset: 0,
        });
        const data = await response.json();
        setTags(data.tags || []);
        setFilteredTags(data.tags || []);
      } catch (err) {
        setError(err.message);
      }
    };
    fetchTags();
  }, [orgId, params, isStorybook, existingTags]);

  // Update filtered tags based on the search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTags(tags);
    } else {
      setFilteredTags(tags.filter((tag) => tag.name.toLowerCase().includes(searchQuery.toLowerCase())));
    }
  }, [searchQuery, tags]);

  const handleCheckbox = async (tag) => {
    const isExistingTags = existingTags.some((t) => t.id === tag.id); // Is tag in Existing Array
    const isTagsToAdd = tagsToAdd.some((t) => t.id === tag.id); // Is tag in tagsToAdd Array
    const isTagsToRemove = tagsToRemove.some((t) => t.id === tag.id); // Is tag in tagsToRemove Array
    const isAssignedToAllSelectedSites = isExistingTags && existingTags.find((t) => t.id === tag.id).sites && existingTags.find((t) => t.id === tag.id).sites.length === siteIds.length; // If tag associated with all selected sites

    /* If user Unchecked an associated tag */
    if (isExistingTags && !isTagsToRemove) {
      setTagsToAdd(tagsToAdd.filter((t) => t.id !== tag.id)); // Remove it from tagsToAdd
      setTagsToRemove((prevTagsToRemove) => [...prevTagsToRemove, tag]); // Add it to tagsToRemove

      /* If user unchecked an unassociated tag, that happen when user checked unassociated tag then re-unchecked it */
    } else if (isTagsToAdd && !isExistingTags) {
      setTagsToAdd(tagsToAdd.filter((t) => t.id !== tag.id)); // Remove it from tagsToAdd

      /* If user checked an unassociated tag */
    } else if (!isTagsToRemove && !isTagsToAdd) {
      setTagsToAdd((prevTagsToAdd) => [...prevTagsToAdd, tag]); // Add it to tagsToAdd
      setTagsToRemove(tagsToRemove.filter((t) => t.id !== tag.id)); // Remove it from tagsToRemove

      /* If user checked an associated tag, that happen when user unchecked existing tag then rechecked it */
    } else if (isTagsToRemove) {
      if (isTagShowPage && !isAssignedToAllSelectedSites) {
        setTagsToAdd((prevTagsToAdd) => [...prevTagsToAdd, tag]); // Add it to tagsToAdd
      }
      setTagsToRemove(tagsToRemove.filter((t) => t.id !== tag.id)); // Remove it from tagsToRemove
      // In case tag not common in all selected sites, tag will associate with all selected sites
      setUpdatedNonCommonTags((prev) => Array.from(new Set([...prev, tag.id])));
    }
    setError('');
    setInlineError('');
  };

  // Handle applying selected tags
  const handleApply = async () => {
    setIsSubmitting(true);
    setError('');
    setInlineError('');
    setIsUnsavedChanges(false);
    handleClose();

    try {
      // Remove unchecked tags
      // If parent component is TagShow
      if (isTagShowPage && tagsToRemove.length) {
        const removeTagPromises = tagsToRemove.map((tagArg) => {
          return Promise.all(
            siteIds.map(async (siteId) => {
              if (existingTags.find(currentTag => currentTag.id === tagArg.id)?.sites.includes(String(siteId))) {
                try {
                  const response = await removeTag(tagArg.id, siteId, orgId);
                  if (!response.ok) {
                    throw new Error(`Failed to remove tag: ${response.statusText}`);
                  }
                  return await response.json();
                } catch (err) {
                  showToast({ message: err.message, type: 'error' });
                }
              }
              return Promise.resolve(); // Return a resolved promise if the condition is not met
            }),
          );
        });
        await Promise.all(removeTagPromises);

        // If parent component is SiteDetailsPage
      } else if (!isTagShowPage && tagsToRemove.length) {
        tagsToRemove.map((tag) => onDelete(tag));
      }

      // Add Checked Tags
      if (tagsToAdd.length && siteIds[0]) {
        const tagIds = tagsToAdd.map(tag => tag.id);

        await handleBulkTagsAndSitesAssociation(orgId, tagIds, siteIds);

        if (!isTagShowPage) {
          onCreate(tagsToAdd.flat());
        }
      } else {
        onCreate(tagsToAdd);
      }
      if (tagsToAdd.length || tagsToRemove.length) {
        const sitesCount = siteIds?.length || 0;

        if (onTagsUpdated) {
          onTagsUpdated(sitesCount);
        } else {
          showToast({ message: 'Tag(s) has been successfully updated for the selected site(s)', type: 'success' });
        }
      } else {
        showToast({ message: 'No changes have been made to the tags', type: 'info' });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (tagsToRemove.some(tag => tag.id === isTagShowPage) && isAllSelected) {
        redirectToTags();
      } else if (tagsToRemove.some(tag => tag.id === isTagShowPage) && !isAllSelected) {
        window.location.reload();
      } else {
        setIsSubmitting(false);
        handleClose();
      }
    }
  };

  // Handle form submission for creating a new tag
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');
    setInlineError('');
    setIsUnsavedChanges(false);

    try {
      let tag;
      if (siteIds && siteIds[0]) {
        // If siteId is present, create and associate the tag with the site
        // trying to create tag data and stop execution the function if error occurs
        try {
          tag = await handleCreateAndAssociateTag(tagName, orgId, siteIds, setIsSubmitting, handleClose);
          if (tag) {
            showToast({ message: `The ${tagName} tag has been created and associated with selected sites`, type: 'success' });
          }
        } catch (err) {
          setInlineError(err.message); // error for tag created tab
          setIsSubmitting(false);
          return;
        }

        onCreate(tag);
        const isCloseOnCreate = true;
        handleClose(isCloseOnCreate);
      } else {
        // If no siteId is present, creates the tag without associating it to a site
        // creating tag data
        tag = {
          name: tagName,
        };
        handleCreatedNotAssociatedTag(tag, onCreate, orgId, showToast, setInlineError, setIsSubmitting, handleClose);
      }
    } catch (err) {
      setInlineError(err.message);// error for tag created tab
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    setTagName(event.target.value);
    setError('');
    setInlineError('');
  };

  const tabs = {};

  if (isTagAsTabShowing) {
    tabs.tagAs = {
      label: 'Tag as',
      content: (
        <>
          {/* search input */}
          <div className="px-3 pt-0.5 relative">
            <input type="text" placeholder="" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full py-1 pr-9.5 border-b border-vamos-blue-gray rounded-none focus:outline-none bg-vamos-white" />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pt-2 cursor-pointer">
              <SearchIcon className="text-vamos-gray" />
            </div>
          </div>
          {/* list of existing tags */}
          <div className="max-h-100 overflow-y-auto pt-10 mb-7">
            <ul className="space-y-2 ml-3">
              {filteredTags.map((tag) => {
                const isExistingTags = existingTags.some((t) => t.id === tag.id);
                const isTagToAdd = tagsToAdd.some((t) => t.id === tag.id);
                const isTagsToRemove = tagsToRemove.some((t) => t.id === tag.id);
                const isSomeSiteAssociated = existingTags.find(existingTag => existingTag.id === tag.id)
                  && existingTags.find(existingTag => existingTag.id === tag.id).sites && existingTags.find(existingTag => existingTag.id === tag.id).sites.length !== siteIds.length && !updatedNonCommonTags.includes(tag.id);

                return (
                  <button
                    key={tag.id}
                    type="button"
                    className="flex items-center cursor-pointer "
                    onClick={() => handleCheckbox(tag)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { handleCheckbox(tag); } }}
                  >
                    <div
                      className={`${isTagToAdd || (isExistingTags && !isTagsToRemove)
                        ? 'bg-vamos-black'
                        : 'bg-vamos-true-white'}
                        w-5 h-5 flex items-center justify-center border border-vamos-black rounded`}
                    >
                      {isSomeSiteAssociated
                        ? (<img src={DashIcon} alt="Dash" className="w-4 h-4" />)
                        : (<img src={DoneIcon} alt="Done" className="w-4 h-4" />)}
                    </div>
                    <label className="pl-3">{tag.name}</label>
                  </button>
                );
              })}
            </ul>
          </div>
          {error && <p className="text-vamos-red text-sm text-left">{error}</p>}
          {/* button apply */}
          <div className="flex justify-end items-center pl-5">
            <Button
              label={isSubmitting ? 'Applying...' : 'Apply'}
              type="apply"
              onClick={handleApply}
              className="mb-4"
              isDisabled={isSubmitting}
              size="md"
            />
          </div>
        </>
      ),
    };
  }

  if (isCreateNewTabShowing) {
    tabs.createNew = {
      label: 'Create new',
      content: (
        <form id="create_new_tag" className={`flex flex-col ${inlineError.length > 0 ? 'space-y-1' : 'space-y-4'}`} onSubmit={handleSubmit}>
          <div className="flex flex-col space-y-4">
            <label htmlFor="name" className="text-left font-medium ml-4">
              Name <span className="text-vamos-green4">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={tagName}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className={`border rounded p-1.5 focus:outline-none focus:ring-2 focus:ring-black mt-0 ml-4 mr-4 ${inlineError && 'border-vamos-red'}`}
            />
          </div>
          {inlineError && <p className="text-vamos-red ml-4 mb-3 text-left text-sm">{inlineError}</p>}
          <div className="flex justify-end">
            <Button
              label={isSubmitting ? 'Creating...' : 'Create'}
              type="submit"
              className="mb-4"
              isDisabled={isSubmitting}
              size="md"
            />
          </div>
        </form>
      ),
    };
  }

  return (
    <MultipleTabsModal
      title={title}
      handleClose={handleClose}
      tabs={tabs}
      handleCloseDialog={handleCloseDialog}
    />
  );
};

TagModal.propTypes = {
  isStorybook: PropTypes.bool,
  title: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  isTagAsTabShowing: PropTypes.bool.isRequired,
  isCreateNewTabShowing: PropTypes.bool.isRequired,
  orgId: PropTypes.number.isRequired,
  siteIds: PropTypes.arrayOf(PropTypes.number),
  existingTags: PropTypes.arrayOf(PropTypes.object),
  onDelete: PropTypes.func,
  isTagShowPage: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  params: PropTypes.shape({
    includeSiteData: PropTypes.bool,
    name: PropTypes.string,
    sortBy: PropTypes.string,
    direction: PropTypes.string,
    limit: PropTypes.number,
    offset: PropTypes.number,
  }),
  isAllSelected: PropTypes.bool,
  onTagsUpdated: PropTypes.func,
};

export default TagModal;
